import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { usersApi } from '../../api/users';
import { Wallet, Check, X, AlertCircle, Filter, Search, Loader, Eye, Clock, CheckCircle, XCircle, DollarSign, User, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const AdminWithdrawals = () => {
  const { locale } = useLanguage();
  const [withdrawals, setWithdrawals] = useState([]);
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 6;
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    userId: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
    currency: ''
  });
  
  // Selected withdrawal for details/actions
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [skrillEmail, setSkrillEmail] = useState('');
  const [savingSkrill, setSavingSkrill] = useState(false);
  const [wiseEmail, setWiseEmail] = useState('');
  const [savingWise, setSavingWise] = useState(false);
  const [cryptoPayWithdrawalId, setCryptoPayWithdrawalId] = useState(null);

  useEffect(() => {
    setCurrentPage(0);
    loadData();
  }, [filters]);

  useEffect(() => {
    if (allWithdrawals.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setWithdrawals(allWithdrawals.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [withdrawalsResponse, statsResponse] = await Promise.all([
        adminApi.getWithdrawals(filters),
        adminApi.getWithdrawalStats()
      ]);

      if (withdrawalsResponse?.success) {
        const allWithdrawalsData = withdrawalsResponse.withdrawals || [];
        setAllWithdrawals(allWithdrawalsData);
        const start = currentPage * ITEMS_PER_PAGE;
        setWithdrawals(allWithdrawalsData.slice(start, start + ITEMS_PER_PAGE));
      } else {
        showMessage('error', withdrawalsResponse?.error || 'Failed to load withdrawals');
        setWithdrawals([]);
        setAllWithdrawals([]);
      }
      
      if (statsResponse?.success) {
        setStats(statsResponse.stats);
      } else {
        console.warn('Failed to load withdrawal stats:', statsResponse?.error);
      }
    } catch (err) {
      console.error('Error loading withdrawals:', err);
      let errorMessage = 'Failed to load withdrawals';
      
      if (err.message === 'Failed to fetch' || err.message.includes('NetworkError') || err.message.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check if the backend is running.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      showMessage('error', errorMessage);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedWithdrawal || !selectedWithdrawal.id) {
      console.error('Invalid withdrawal selected:', selectedWithdrawal);
      showMessage('error', 'Invalid withdrawal selected');
      return;
    }

    const withdrawalId = parseInt(selectedWithdrawal.id);
    if (isNaN(withdrawalId)) {
      console.error('Invalid withdrawal ID:', selectedWithdrawal.id);
      showMessage('error', 'Invalid withdrawal ID');
      return;
    }

    console.log('Approving withdrawal with ID:', withdrawalId);
    setLoading(true);
    try {
      const response = await adminApi.approveWithdrawal(withdrawalId, null);
      if (response?.success) {
        showMessage('success', `Withdrawal ${withdrawalId} approved successfully`);
        setShowApproveModal(false);
        setSelectedWithdrawal(null);
        setSkrillEmail('');
        setWiseEmail('');
        loadData();
      } else {
        showMessage('error', response?.error || 'Failed to approve withdrawal');
      }
    } catch (err) {
      console.error('Error approving withdrawal:', err);
      showMessage('error', err.response?.data?.error || err.message || 'Failed to approve withdrawal');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSkrillEmail = async () => {
    if (!selectedWithdrawal?.userId || !skrillEmail.trim()) {
      showMessage('error', 'Insert a Skrill email');
      return;
    }

    setSavingSkrill(true);
    try {
      const response = await usersApi.updateSkrillEmail(selectedWithdrawal.userId, skrillEmail.trim());
      if (response?.success) {
        showMessage('success', 'Skrill email updated');
        loadData();
      } else {
        showMessage('error', response?.error || 'Failed to update Skrill email');
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || err.message || 'Failed to update Skrill email');
    } finally {
      setSavingSkrill(false);
    }
  };

  const handleSaveWiseEmail = async () => {
    if (!selectedWithdrawal?.userId || !wiseEmail.trim()) {
      showMessage('error', 'Insert a Wise email');
      return;
    }

    setSavingWise(true);
    try {
      const response = await usersApi.updateWiseEmail(selectedWithdrawal.userId, wiseEmail.trim());
      if (response?.success) {
        showMessage('success', 'Wise email updated');
        loadData();
      } else {
        showMessage('error', response?.error || 'Failed to update Wise email');
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || err.message || 'Failed to update Wise email');
    } finally {
      setSavingWise(false);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal || !selectedWithdrawal.id) {
      console.error('Invalid withdrawal selected:', selectedWithdrawal);
      showMessage('error', 'Invalid withdrawal selected');
      return;
    }

    if (!rejectReason.trim()) {
      showMessage('error', 'Please provide a reason for rejection');
      return;
    }

    const withdrawalId = parseInt(selectedWithdrawal.id);
    if (isNaN(withdrawalId)) {
      console.error('Invalid withdrawal ID:', selectedWithdrawal.id);
      showMessage('error', 'Invalid withdrawal ID');
      return;
    }

    console.log('Rejecting withdrawal with ID:', withdrawalId);
    setLoading(true);
    try {
      const response = await adminApi.rejectWithdrawal(withdrawalId, rejectReason);
      if (response?.success) {
        showMessage('success', `Withdrawal ${withdrawalId} rejected. Amount refunded to user.`);
        setShowRejectModal(false);
        setSelectedWithdrawal(null);
        setRejectReason('');
        loadData();
      } else {
        showMessage('error', response?.error || 'Failed to reject withdrawal');
      }
    } catch (err) {
      console.error('Error rejecting withdrawal:', err);
      showMessage('error', err.response?.data?.error || err.message || 'Failed to reject withdrawal');
    } finally {
      setLoading(false);
    }
  };

  const canTriggerCryptoPay = (w) =>
    w.status === 'completed' &&
    w.currency === 'USDT' &&
    w.toPayPayoutMethod === 'crypto' &&
    w.toPayStatus === 'pending';

  const handleCryptoPay = async (withdrawal) => {
    const withdrawalId = parseInt(withdrawal.id, 10);
    if (isNaN(withdrawalId)) {
      showMessage('error', 'Invalid withdrawal ID');
      return;
    }
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    setCryptoPayWithdrawalId(withdrawalId);
    try {
      try {
        await adminApi.triggerWithdrawalCryptoPay(withdrawalId);
      } catch (err) {
        if (err.status === 409) {
          showMessage('info', 'Payout already running; waiting for completion…');
        } else {
          throw err;
        }
      }

      let last = null;
      for (let i = 0; i < 160; i++) {
        await sleep(2000);
        last = await adminApi.getWithdrawalCryptoPayStatus(withdrawalId);
        if (last.status !== 'running') break;
      }

      const r = last?.result;
      if (r?.success) {
        const tx = r.transactionHash ? ` Tx: ${r.transactionHash}` : '';
        showMessage('success', `On-chain payout completed.${tx}`);
      } else if (last?.status === 'running') {
        showMessage('error', 'Payout still running — check again later or logs on eventsnpayment.');
      } else {
        showMessage('error', r?.error || last?.error || 'Crypto payout failed');
      }
      await loadData(true);
    } catch (err) {
      console.error('Crypto pay error:', err);
      showMessage('error', err.data?.error || err.message || 'Crypto payout failed');
    } finally {
      setCryptoPayWithdrawalId(null);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString(locale || 'it-IT');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-900/50', text: 'text-yellow-300', icon: Clock, label: 'Pending' },
      completed: { bg: 'bg-green-900/50', text: 'text-green-300', icon: CheckCircle, label: 'Completed' },
      failed: { bg: 'bg-red-900/50', text: 'text-red-300', icon: XCircle, label: 'Failed' },
      cancelled: { bg: 'bg-gray-900/50', text: 'text-gray-300', icon: XCircle, label: 'Cancelled' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Wallet className="w-8 h-8 mr-3 text-yellow-400" />
          Withdrawals Management
        </h1>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-900/50 border-green-700 text-green-200' 
            : 'bg-red-900/50 border-red-700 text-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <Check className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Pending</span>
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-2xl font-bold text-yellow-400">{stats.pending.count}</div>
            <div className="text-xs text-gray-500 mt-1">{formatCurrency(stats.pending.total)} DIOS</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Completed (30d)</span>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-2xl font-bold text-green-400">{stats.completed.count}</div>
            <div className="text-xs text-gray-500 mt-1">{formatCurrency(stats.completed.total)} DIOS</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Rejected (30d)</span>
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-2xl font-bold text-red-400">{stats.rejected.count}</div>
            <div className="text-xs text-gray-500 mt-1">{formatCurrency(stats.rejected.total)} DIOS</div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Total (All Time)</span>
              <DollarSign className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-2xl font-bold text-blue-400">{stats.total.count}</div>
            <div className="text-xs text-gray-500 mt-1">{formatCurrency(stats.total.total)} DIOS</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 mb-6">
        <div className="flex items-center mb-4">
          <Filter className="w-5 h-5 mr-2 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-300">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">User ID</label>
            <input
              type="number"
              value={filters.userId}
              onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
              placeholder="Filter by user ID"
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
            <select
              value={filters.currency}
              onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Currencies</option>
              <option value="DIOS10">DIOS</option>
              <option value="EUR">EUR</option>
              <option value="USDT">USDT</option>
              <option value="USDC">USDC</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Min Amount</label>
            <input
              type="number"
              value={filters.minAmount}
              onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
              placeholder="Min amount"
              step="0.01"
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Max Amount</label>
            <input
              type="number"
              value={filters.maxAmount}
              onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
              placeholder="Max amount"
              step="0.01"
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                status: '',
                userId: '',
                dateFrom: '',
                dateTo: '',
                minAmount: '',
                maxAmount: '',
                currency: ''
              })}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Withdrawals Table */}
      {loading ? (
        <div className="text-center py-12">
          <Loader className="w-8 h-8 animate-spin mx-auto text-indigo-400" />
          <p className="mt-4 text-gray-400">Loading withdrawals...</p>
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          No withdrawals found
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Payroll</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {withdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id} className="hover:bg-gray-900/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      #{withdrawal.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-white">{withdrawal.user.username}</div>
                        <div className="text-gray-400 text-xs">ID: {withdrawal.userId}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-400">
                        {formatCurrency(withdrawal.amount)} {withdrawal.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(withdrawal.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {withdrawal.canWithdraw ? (
                        <span className="text-xs text-green-400">✓ Eligible</span>
                      ) : (
                        <span className="text-xs text-red-400">
                          ✗ Need {formatCurrency(withdrawal.payrollStatus.remaining)} more
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(withdrawal.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedWithdrawal(withdrawal);
                            setSkrillEmail(withdrawal.user?.skrillEmail || '');
                            setShowDetailsModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {canTriggerCryptoPay(withdrawal) && (
                          <button
                            type="button"
                            onClick={() => handleCryptoPay(withdrawal)}
                            disabled={loading || cryptoPayWithdrawalId === withdrawal.id}
                            className="p-2 text-gray-400 hover:text-emerald-400 transition-colors disabled:opacity-40"
                            title="Pay USDT on-chain (to_pay pending)"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        )}
                        {withdrawal.status === 'pending' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedWithdrawal(withdrawal);
                                setSkrillEmail(withdrawal.user?.skrillEmail || '');
                                setWiseEmail(withdrawal.user?.wiseEmail || '');
                                setShowApproveModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedWithdrawal(withdrawal);
                                setShowRejectModal(true);
                              }}
                              className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Withdrawal Details</h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedWithdrawal(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Withdrawal ID</label>
                    <div className="text-white font-medium">#{selectedWithdrawal.id}</div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Status</label>
                    <div>{getStatusBadge(selectedWithdrawal.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Amount</label>
                    <div className="text-green-400 font-medium text-lg">
                      {formatCurrency(selectedWithdrawal.amount)} {selectedWithdrawal.currency}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Date</label>
                    <div className="text-white">{formatDate(selectedWithdrawal.createdAt)}</div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    User Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400">Username</label>
                      <div className="text-white">{selectedWithdrawal.user.username}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Email</label>
                      <div className="text-white">{selectedWithdrawal.user.email || 'N/A'}</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Wallet Address</label>
                      <div className="text-white font-mono text-xs break-all">
                        {selectedWithdrawal.user.walletAddress || 'N/A'}
                      </div>
                    </div>
                  <div>
                    <label className="text-sm text-gray-400">Skrill Email</label>
                    <div className="text-white">
                      {selectedWithdrawal.user.skrillEmail || 'N/A'}
                    </div>
                  </div>
                    <div>
                      <label className="text-sm text-gray-400">Current Balance</label>
                      <div className="text-green-400 font-medium">
                        {formatCurrency(selectedWithdrawal.user.balance)} DIOS
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <h3 className="text-lg font-semibold text-white mb-4">Payroll Status</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-400">Total Deposited</label>
                      <div className="text-white">{formatCurrency(selectedWithdrawal.payrollStatus.totalDeposited)} DIOS</div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-400">Total Spent</label>
                      <div className="text-white">{formatCurrency(selectedWithdrawal.payrollStatus.totalSpent)} DIOS</div>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm text-gray-400">Remaining to Spend</label>
                      <div className={`font-medium ${
                        selectedWithdrawal.canWithdraw ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formatCurrency(selectedWithdrawal.payrollStatus.remaining)} DIOS
                      </div>
                      {selectedWithdrawal.canWithdraw ? (
                        <p className="text-xs text-green-400 mt-1">✓ User meets payroll requirement</p>
                      ) : (
                        <p className="text-xs text-red-400 mt-1">✗ User must spend more before withdrawal</p>
                      )}
                    </div>
                  </div>
                </div>

                {selectedWithdrawal.transactionHash && (
                  <div className="border-t border-gray-700 pt-4">
                    <label className="text-sm text-gray-400">Transaction Hash</label>
                    <div className="text-white font-mono text-xs break-all bg-gray-900 p-2 rounded">
                      {selectedWithdrawal.transactionHash}
                    </div>
                  </div>
                )}

                {selectedWithdrawal.description && (
                  <div className="border-t border-gray-700 pt-4">
                    <label className="text-sm text-gray-400">Description</label>
                    <div className="text-white">{selectedWithdrawal.description}</div>
                  </div>
                )}

                <div className="border-t border-gray-700 pt-4">
                  <label className="text-sm text-gray-400">Update Skrill Email</label>
                  <div className="flex items-center space-x-2 mt-2">
                    <input
                      type="email"
                      value={skrillEmail}
                      onChange={(e) => setSkrillEmail(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="name@example.com"
                    />
                    <button
                      onClick={handleSaveSkrillEmail}
                      disabled={savingSkrill}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50"
                    >
                      {savingSkrill ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-4">
                {selectedWithdrawal.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setShowRejectModal(true);
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setShowApproveModal(true);
                      }}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedWithdrawal(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal */}
      {showApproveModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-400" />
                  Approve Withdrawal
                </h2>
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedWithdrawal(null);
                    setSkrillEmail('');
                    setWiseEmail('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Withdrawal ID</div>
                  <div className="text-white font-medium">#{selectedWithdrawal.id}</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">User</div>
                  <div className="text-white font-medium">{selectedWithdrawal.user.username}</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Amount</div>
                  <div className="text-green-400 font-medium text-lg">
                    {formatCurrency(selectedWithdrawal.amount)} {selectedWithdrawal.currency}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Skrill Email (for EUR)
                  </label>
                  <input
                    type="email"
                    value={skrillEmail}
                    onChange={(e) => setSkrillEmail(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="name@example.com"
                    disabled={selectedWithdrawal.currency !== 'EUR'}
                  />
                </div>
                <p className="text-xs text-gray-400">
                  Skrill required for EUR payouts
                </p>

                {!selectedWithdrawal.canWithdraw && (
                  <div className="bg-red-900/50 border border-red-700 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
                      <div className="text-sm text-red-300">
                        User does not meet payroll requirement. Cannot approve.
                      </div>
                    </div>
                  </div>
                )}

              </div>

              <div className="mt-6 flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedWithdrawal(null);
                    setSkrillEmail('');
                    setWiseEmail('');
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={loading || !selectedWithdrawal.canWithdraw}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <XCircle className="w-5 h-5 mr-2 text-red-400" />
                  Reject Withdrawal
                </h2>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedWithdrawal(null);
                    setRejectReason('');
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Withdrawal ID</div>
                  <div className="text-white font-medium">#{selectedWithdrawal.id}</div>
                </div>

                <div className="bg-gray-900/50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Amount</div>
                  <div className="text-green-400 font-medium">
                    {formatCurrency(selectedWithdrawal.amount)} {selectedWithdrawal.currency}
                  </div>
                </div>

                <div className="bg-yellow-900/50 border border-yellow-700 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-yellow-400 mr-2" />
                    <div className="text-sm text-yellow-300">
                      The withdrawal amount will be refunded to the user's balance.
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Reason for Rejection <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={4}
                    placeholder="Enter reason for rejecting this withdrawal..."
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedWithdrawal(null);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={loading || !rejectReason.trim()}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Rejecting...
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && allWithdrawals.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-center space-x-4 mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage === 0
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </button>
          <span className="text-gray-400 text-sm">
            Page {currentPage + 1} of {Math.ceil(allWithdrawals.length / ITEMS_PER_PAGE)}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(Math.ceil(allWithdrawals.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
            disabled={currentPage >= Math.ceil(allWithdrawals.length / ITEMS_PER_PAGE) - 1}
            className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
              currentPage >= Math.ceil(allWithdrawals.length / ITEMS_PER_PAGE) - 1
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >
            Next
            <ChevronRight className="w-5 h-5 ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawals;
